import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Phone, Menu, X, Calendar, Clock, MapPin, ChevronDown } from 'lucide-react';
import { treatmentAreas } from '@/data/treatmentAreas';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_handcare-redesign/artifacts/75wjnq72_Untitled-design.webp";

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isTreatmentDropdownOpen, setIsTreatmentDropdownOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsTreatmentDropdownOpen(false);
  }, [location]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const navLinks = [
    { label: 'Treatment Areas', href: '/treatment-areas', hasDropdown: true },
    { label: 'Doctors', href: '/doctors' },
    { label: 'Patient Resources', href: '/patient-resources' },
    { label: 'Contact Us', href: '/contact' },
  ];

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  return (
    <>
      {/* Top Bar - Desktop Only */}
      <div className="bg-primary text-primary-foreground py-2 hidden md:block">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center text-sm">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>Mon-Fri: 8:00 AM - 5:00 PM</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              <span>2010 W. Eau Gallie Blvd, Ste 104, Melbourne, FL</span>
            </div>
          </div>
          <a 
            href="tel:321-500-4263" 
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <Phone className="w-4 h-4" />
            <span className="font-semibold">321.500.HAND (4263)</span>
          </a>
        </div>
      </div>

      {/* Main Navbar */}
      <nav 
        className={`sticky top-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? 'bg-card/95 backdrop-blur-md shadow-lg' 
            : 'bg-card'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0">
              <img 
                src={LOGO_URL} 
                alt="Melbourne Hand Center" 
                className="h-10 md:h-16 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <div key={link.label} className="relative">
                  {link.hasDropdown ? (
                    <div 
                      className="relative"
                      onMouseEnter={() => setIsTreatmentDropdownOpen(true)}
                      onMouseLeave={() => setIsTreatmentDropdownOpen(false)}
                    >
                      <Link
                        to={link.href}
                        className={`flex items-center gap-1 font-medium transition-colors relative group ${
                          isActive(link.href) ? 'text-primary' : 'text-foreground/80 hover:text-primary'
                        }`}
                      >
                        {link.label}
                        <ChevronDown className={`w-4 h-4 transition-transform ${isTreatmentDropdownOpen ? 'rotate-180' : ''}`} />
                        <span className={`absolute -bottom-1 left-0 h-0.5 bg-primary transition-all duration-300 ${
                          isActive(link.href) ? 'w-full' : 'w-0 group-hover:w-full'
                        }`} />
                      </Link>
                      
                      {/* Dropdown Menu */}
                      {isTreatmentDropdownOpen && (
                        <div className="absolute top-full left-0 mt-2 w-72 bg-card rounded-xl shadow-xl border border-border py-2 animate-fade-in">
                          <div className="max-h-96 overflow-y-auto">
                            {treatmentAreas.map((treatment) => (
                              <Link
                                key={treatment.slug}
                                to={`/treatment-areas/${treatment.slug}`}
                                className="block px-4 py-2 text-sm text-foreground/80 hover:bg-primary/10 hover:text-primary transition-colors"
                              >
                                {treatment.title}
                              </Link>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link
                      to={link.href}
                      className={`font-medium transition-colors relative group ${
                        isActive(link.href) ? 'text-primary' : 'text-foreground/80 hover:text-primary'
                      }`}
                    >
                      {link.label}
                      <span className={`absolute -bottom-1 left-0 h-0.5 bg-primary transition-all duration-300 ${
                        isActive(link.href) ? 'w-full' : 'w-0 group-hover:w-full'
                      }`} />
                    </Link>
                  )}
                </div>
              ))}
            </div>

            {/* CTA Buttons - Desktop */}
            <div className="hidden lg:flex items-center gap-4">
              <a href="tel:321-500-4263" className="flex items-center gap-2 text-primary font-semibold">
                <Phone className="w-5 h-5" />
                <span>321.500.4263</span>
              </a>
              <Button 
                variant="hero" 
                size="lg"
                asChild
              >
                <a 
                  href="https://app.acuityscheduling.com/schedule.php?owner=13861726" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2"
                >
                  <Calendar className="w-5 h-5" />
                  Schedule Now
                </a>
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 text-foreground z-50"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        {isMobileMenuOpen && (
          <div 
            className="lg:hidden fixed inset-0 top-16 bg-card z-40 overflow-y-auto"
            style={{ height: 'calc(100vh - 4rem)' }}
          >
            <div className="px-4 py-6 space-y-2">
              {navLinks.map((link) => (
                <div key={link.label} className="border-b border-border last:border-0">
                  {link.hasDropdown ? (
                    <div>
                      <button
                        onClick={() => setIsTreatmentDropdownOpen(!isTreatmentDropdownOpen)}
                        className="flex items-center justify-between w-full py-4 text-foreground font-medium text-lg"
                      >
                        {link.label}
                        <ChevronDown className={`w-5 h-5 transition-transform ${isTreatmentDropdownOpen ? 'rotate-180' : ''}`} />
                      </button>
                      {isTreatmentDropdownOpen && (
                        <div className="pb-4 space-y-1 max-h-64 overflow-y-auto">
                          <Link
                            to="/treatment-areas"
                            className="block py-2 px-4 text-primary font-medium bg-primary/5 rounded-lg"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            View All Treatment Areas
                          </Link>
                          {treatmentAreas.map((treatment) => (
                            <Link
                              key={treatment.slug}
                              to={`/treatment-areas/${treatment.slug}`}
                              className="block py-2 px-4 text-foreground/70 hover:text-primary hover:bg-primary/5 rounded-lg transition-colors"
                              onClick={() => setIsMobileMenuOpen(false)}
                            >
                              {treatment.title}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link
                      to={link.href}
                      className={`block py-4 font-medium text-lg transition-colors ${
                        isActive(link.href) ? 'text-primary' : 'text-foreground hover:text-primary'
                      }`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {link.label}
                    </Link>
                  )}
                </div>
              ))}
              
              {/* Mobile CTA Section */}
              <div className="pt-6 space-y-4">
                <a 
                  href="tel:321-500-4263" 
                  className="flex items-center justify-center gap-2 text-primary font-semibold text-lg py-3 border border-primary rounded-xl"
                >
                  <Phone className="w-5 h-5" />
                  321.500.HAND (4263)
                </a>
                <Button 
                  variant="hero" 
                  size="lg"
                  className="w-full"
                  asChild
                >
                  <a 
                    href="https://app.acuityscheduling.com/schedule.php?owner=13861726" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2"
                  >
                    <Calendar className="w-5 h-5" />
                    Schedule Appointment
                  </a>
                </Button>
              </div>

              {/* Mobile Contact Info */}
              <div className="pt-6 text-center text-sm text-muted-foreground">
                <p className="flex items-center justify-center gap-2 mb-2">
                  <Clock className="w-4 h-4" />
                  Mon-Fri: 8:00 AM - 5:00 PM
                </p>
                <p className="flex items-center justify-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Melbourne, FL 32935
                </p>
              </div>
            </div>
          </div>
        )}
      </nav>
    </>
  );
};

export default Navbar;
